export const LAYOUT_ENGINE = 'ampnebula-layout';
export const LAYOUT_SCHEMA_VERSION = 1;
export const MM_PER_INCH = 25.4;

const trimMeasurement = (value) => String(Number(value.toFixed(3)));

export const formatMeasurement = (millimeters, units = 'mm') => {
  if (units === 'cm') return trimMeasurement(millimeters / 10);
  if (units !== 'in') return trimMeasurement(millimeters);

  const totalThirtySeconds = Math.round(millimeters / MM_PER_INCH * 32);
  const whole = Math.floor(totalThirtySeconds / 32);
  const numerator = totalThirtySeconds % 32;
  if (numerator === 0) return String(whole);

  const divisor = numerator % 16 === 0 ? 16 : numerator % 8 === 0 ? 8 : numerator % 4 === 0 ? 4 : numerator % 2 === 0 ? 2 : 1;
  const fraction = `${numerator / divisor}/${32 / divisor}`;
  return whole > 0 ? `${whole} ${fraction}` : fraction;
};

export const parseMeasurement = (input, units = 'mm') => {
  const text = String(input).trim();
  if (!text) return null;
  if (units === 'mm') {
    const millimeters = Number(text);
    return Number.isFinite(millimeters) ? millimeters : null;
  }

  const mixed = text.match(/^(-?\d+)\s+(\d+)\s*\/\s*(\d+)$/);
  const fraction = text.match(/^(-?\d+)\s*\/\s*(\d+)$/);
  let measurement;
  if (mixed) {
    const denominator = Number(mixed[3]);
    if (denominator === 0) return null;
    const sign = mixed[1].startsWith('-') ? -1 : 1;
    measurement = sign * (Math.abs(Number(mixed[1])) + Number(mixed[2]) / denominator);
  } else if (fraction) {
    const denominator = Number(fraction[2]);
    if (denominator === 0) return null;
    measurement = Number(fraction[1]) / denominator;
  } else {
    measurement = Number(text);
  }

  if (!Number.isFinite(measurement)) return null;
  return measurement * (units === 'in' ? MM_PER_INCH : 10);
};

export const RESISTOR_BAND_COLORS = Object.freeze({
  0: '#4b5356',
  1: '#a85a32',
  2: '#ef473d',
  3: '#ff8a24',
  4: '#f3d438',
  5: '#35ad6f',
  6: '#4385e2',
  7: '#9a67c7',
  8: '#9aa1a3',
  9: '#fffdf2',
  gold: '#d9af38',
  silver: '#c9cecc',
});

export const BRIGHT_WIRE_COLORS = Object.freeze([
  '#ef473d',
  '#f1bd32',
  '#35ad6f',
  '#4385e2',
  '#596366',
  '#f7f8f4',
]);

export const getResistorBandColors = (value, significantDigits = 2) => {
  const match = String(value || '').trim().match(/^(\d+(?:\.\d+)?)\s*([kKmM]?)(?:Ω|ohms?)?$/i);
  const fallbackDigits = significantDigits === 3 ? [1, 0, 0] : [1, 0];
  const fallback = [...fallbackDigits.map(digit => RESISTOR_BAND_COLORS[digit]), RESISTOR_BAND_COLORS[4], RESISTOR_BAND_COLORS.gold];
  if (!match) return fallback;

  const scale = match[2].toLowerCase() === 'm' ? 1000000 : match[2].toLowerCase() === 'k' ? 1000 : 1;
  const ohms = Number(match[1]) * scale;
  if (!Number.isFinite(ohms) || ohms <= 0) return fallback;

  let multiplier = Math.floor(Math.log10(ohms)) - (significantDigits - 1);
  let significant = Math.round(ohms / (10 ** multiplier));
  if (significant >= 10 ** significantDigits) {
    significant = Math.round(significant / 10);
    multiplier += 1;
  }
  multiplier = Math.max(-2, Math.min(9, multiplier));
  const multiplierColor = multiplier === -2
    ? RESISTOR_BAND_COLORS.silver
    : multiplier === -1
      ? RESISTOR_BAND_COLORS.gold
      : RESISTOR_BAND_COLORS[multiplier];

  const digits = String(significant).padStart(significantDigits, '0').split('').map(Number);
  return [...digits.map(digit => RESISTOR_BAND_COLORS[digit]), multiplierColor, RESISTOR_BAND_COLORS.gold];
};

const DEFAULT_CANVAS = Object.freeze({
  width: 420,
  height: 297,
  grid: 5,
});

const DEFAULT_SETTINGS = Object.freeze({
  resistorDisplay: 'bands',
  displayUnits: 'cm',
  printCalibration: false,
  showRulers: false,
});

export const RESISTOR_LIBRARY = Object.freeze({
  'carbon-composition': Object.freeze({ name: 'Carbon composition', value: '100kΩ', wattage: 0.5, bodyScale: 0.8, width: 36, height: 12 }),
  'iskra-upm': Object.freeze({ name: 'ISKRA UPM', value: '100kΩ', wattage: 0.5, width: 36, height: 12 }),
  piher: Object.freeze({ name: 'Piher', value: '100kΩ', wattage: 0.5, width: 36, height: 12 }),
  'ceramic-composition': Object.freeze({ name: 'Ceramic composition', value: '47kΩ', wattage: 2, bodyScale: 1, width: 48, height: 14 }),
  'metal-film': Object.freeze({ name: 'Metal film', value: '100kΩ', wattage: 0.5, bodyScale: 1, width: 42, height: 12 }),
  'metal-oxide': Object.freeze({ name: 'Metal oxide', value: '1kΩ', wattage: 2, bodyScale: 1, width: 48, height: 14 }),
});

export const DEFAULT_CAN_CAPACITOR_SECTIONS = Object.freeze([
  Object.freeze({ id: 'section-a', label: 'Section A', capacitance: '32µF', symbol: 'semicircle' }),
  Object.freeze({ id: 'section-b', label: 'Section B', capacitance: '32µF', symbol: 'triangle' }),
]);

export const CAPACITOR_LIBRARY = Object.freeze({
  'axial-film': Object.freeze({ name: 'Axial film', packageStyle: 'axial', value: '22nF', width: 42, height: 14 }),
  'orange-drop': Object.freeze({ name: 'Orange Drop', packageStyle: 'radial', value: '22nF', width: 28, height: 22 }),
  'ceramic-disc': Object.freeze({ name: 'Ceramic disc', packageStyle: 'radial', value: '500pF', width: 20, height: 22 }),
  'silver-mica': Object.freeze({ name: 'Silver mica', packageStyle: 'radial', value: '500pF', voltageRating: 500, width: 18, height: 30 }),
  'mullard-mustard': Object.freeze({ name: 'Mullard mustard', packageStyle: 'axial', value: '22nF', bodyFraction: 0.7, width: 58, height: 14 }),
  'sozo-coupling': Object.freeze({ name: 'SoZo coupling', packageStyle: 'axial', value: '22nF', bodyFraction: 0.7, width: 62, height: 14 }),
  'board-axial-electrolytic': Object.freeze({ name: 'Board axial electrolytic', packageStyle: 'axial', value: '25µF', voltageRating: 10, sleeveColor: '#eeeade', bodyFraction: 0.62, width: 30, height: 9 }),
  'sprague-30d-electrolytic': Object.freeze({ name: 'Sprague 30D axial electrolytic', packageStyle: 'axial', value: '50µF', voltageRating: 100, bodyFraction: 0.52, width: 52, height: 14 }),
  'axial-electrolytic': Object.freeze({ name: 'Axial electrolytic', packageStyle: 'axial', value: '2200µF', voltageRating: 50, sleeveColor: '#347fa3', bodyFraction: 0.8, width: 68, height: 22 }),
  'radial-electrolytic': Object.freeze({ name: 'Radial electrolytic', packageStyle: 'radial', value: '22µF', voltageRating: 50, sleeveColor: '#347fa3', width: 22, height: 34 }),
  'can-electrolytic': Object.freeze({ name: 'Dual can electrolytic', packageStyle: 'can', value: '32µF / 32µF', voltageRating: 450, width: 42, height: 42 }),
});

const MULLARD_MUSTARD_BODY_SIZES = Object.freeze([
  Object.freeze({ maximumMicrofarads: 0.01, width: 14, height: 5.5 }),
  Object.freeze({ maximumMicrofarads: 0.022, width: 16, height: 7 }),
  Object.freeze({ maximumMicrofarads: 0.047, width: 22, height: 10.75 }),
  Object.freeze({ maximumMicrofarads: Infinity, width: 31, height: 10 }),
]);

export const parseCapacitanceMicrofarads = (value) => {
  const match = String(value || '').trim().match(/^(\d+(?:\.\d+)?)\s*([pnuµμ]?)f?$/i);
  if (!match) return null;
  const amount = Number(match[1]);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  const unit = match[2].toLowerCase();
  if (unit === 'p') return amount / 1000000;
  if (unit === 'n') return amount / 1000;
  if (unit === 'u' || unit === 'µ' || unit === 'μ') return amount;
  return amount;
};

export const getMullardMustardBodySize = (value) => {
  const microfarads = parseCapacitanceMicrofarads(value);
  if (microfarads == null) return MULLARD_MUSTARD_BODY_SIZES[1];
  return MULLARD_MUSTARD_BODY_SIZES.find(size => microfarads <= size.maximumMicrofarads);
};

const SOZO_COUPLING_BODY_SIZES = Object.freeze([
  Object.freeze({ maximumMicrofarads: 0.001, width: 16.2, height: 5.6 }),
  Object.freeze({ maximumMicrofarads: 0.0022, width: 16.2, height: 6.9 }),
  Object.freeze({ maximumMicrofarads: 0.0047, width: 16.2, height: 7.4 }),
  Object.freeze({ maximumMicrofarads: 0.01, width: 16.2, height: 7.4 }),
  Object.freeze({ maximumMicrofarads: 0.022, width: 16.2, height: 7.4 }),
  Object.freeze({ maximumMicrofarads: 0.033, width: 16, height: 8.4 }),
  Object.freeze({ maximumMicrofarads: 0.047, width: 16.2, height: 10.4 }),
  Object.freeze({ maximumMicrofarads: Infinity, width: 31.2, height: 10.4 }),
]);

export const getSozoCouplingBodySize = (value) => {
  const microfarads = parseCapacitanceMicrofarads(value);
  if (microfarads == null) return SOZO_COUPLING_BODY_SIZES[4];
  return SOZO_COUPLING_BODY_SIZES.find(size => microfarads <= size.maximumMicrofarads);
};

const BOARD_AXIAL_ELECTROLYTIC_SIZES = Object.freeze([
  Object.freeze({ maximumVoltage: 10, width: 30, height: 9 }),
  Object.freeze({ maximumVoltage: 16, width: 34, height: 10 }),
  Object.freeze({ maximumVoltage: 25, width: 38, height: 11 }),
  Object.freeze({ maximumVoltage: 50, width: 44, height: 12 }),
  Object.freeze({ maximumVoltage: 100, width: 52, height: 14 }),
]);

export const getBoardAxialElectrolyticSize = (voltageRating) => {
  const voltage = Number(voltageRating);
  if (!Number.isFinite(voltage) || voltage <= 0) return BOARD_AXIAL_ELECTROLYTIC_SIZES[0];
  return BOARD_AXIAL_ELECTROLYTIC_SIZES.find(size => voltage <= size.maximumVoltage)
    || BOARD_AXIAL_ELECTROLYTIC_SIZES[BOARD_AXIAL_ELECTROLYTIC_SIZES.length - 1];
};

export const DIODE_LIBRARY = Object.freeze({
  rectifier: Object.freeze({ name: 'Rectifier diode', value: '1N4007', bodyWidth: 9, bodyHeight: 4, width: 30, height: 7 }),
  'phase-inverter': Object.freeze({ name: 'Phase inverter diode', value: '1N4148', bodyWidth: 6.5, bodyHeight: 2.8, width: 26, height: 6 }),
});

export const DEFAULT_POWER_TRANSFORMER_TAPS = Object.freeze({
  top: Object.freeze([
    Object.freeze({ id: 'auxiliary-6v3', label: 'Auxiliary', specification: '6.3', color: '#c9cecc' }),
    Object.freeze({ id: 'rectifier-5v', label: 'Rectifier', specification: '5', color: '#f1bd32' }),
    Object.freeze({ id: 'auxiliary-common', label: 'Auxiliary common', specification: '0', color: '#596366' }),
    Object.freeze({ id: 'high-voltage-1', label: 'High voltage', specification: '290', color: '#ef473d' }),
    Object.freeze({ id: 'high-voltage-center-tap', label: 'High voltage center tap', specification: '0', color: '#f1bd32' }),
    Object.freeze({ id: 'high-voltage-2', label: 'High voltage', specification: '290', color: '#ef473d' }),
  ]),
  bottom: Object.freeze([
    Object.freeze({ id: 'primary-240v', label: 'Primary', specification: '240', color: '#a85a32' }),
    Object.freeze({ id: 'primary-120v', label: 'Primary', specification: '120', color: '#ef473d' }),
    Object.freeze({ id: 'primary-common', label: 'Primary common', specification: '0', color: '#596366' }),
    Object.freeze({ id: 'heater-1', label: 'Heater', specification: '3.18', color: '#35ad6f' }),
    Object.freeze({ id: 'heater-center-tap', label: 'Heater center tap', specification: '0', color: '#35ad6f' }),
    Object.freeze({ id: 'heater-2', label: 'Heater', specification: '3.18', color: '#35ad6f' }),
  ]),
});

const cloneDefaultPowerTransformerTaps = () => ({
  top: DEFAULT_POWER_TRANSFORMER_TAPS.top.map(tap => ({ ...tap })),
  bottom: DEFAULT_POWER_TRANSFORMER_TAPS.bottom.map(tap => ({ ...tap })),
});

export const getPowerTransformerTaps = (object) => {
  const defaults = cloneDefaultPowerTransformerTaps();
  if (!object || object.type !== 'power-transformer') return defaults;
  return {
    top: Array.isArray(object.taps?.top) ? object.taps.top : defaults.top,
    bottom: Array.isArray(object.taps?.bottom) ? object.taps.bottom : defaults.bottom,
  };
};

export const createLayoutDocument = (overrides = {}) => ({
  engine: LAYOUT_ENGINE,
  schemaVersion: LAYOUT_SCHEMA_VERSION,
  units: 'mm',
  canvas: { ...DEFAULT_CANVAS, ...overrides.canvas },
  settings: {
    ...DEFAULT_SETTINGS,
    resistorDisplay: overrides.settings?.resistorDisplay === 'text' ? 'text' : 'bands',
    displayUnits: overrides.settings?.displayUnits === 'in' ? 'in' : 'cm',
    printCalibration: overrides.settings?.printCalibration === true,
    showRulers: overrides.settings?.showRulers === true,
  },
  objects: Array.isArray(overrides.objects) ? overrides.objects : [],
  conductors: Array.isArray(overrides.conductors) ? overrides.conductors : [],
  nets: Array.isArray(overrides.nets) ? overrides.nets : [],
  layers: Array.isArray(overrides.layers) ? overrides.layers : [],
  groups: Array.isArray(overrides.groups) ? overrides.groups : [],
});

export const normalizeLayoutDocument = (value) => {
  if (!value || value.engine !== LAYOUT_ENGINE) return createLayoutDocument();

  const objectIds = new Set(Array.isArray(value.objects) ? value.objects.map(object => object.id) : []);
  const claimedIds = new Set();
  const groups = (Array.isArray(value.groups) ? value.groups : []).reduce((normalizedGroups, group) => {
    const memberIds = [...new Set(Array.isArray(group.objectIds) ? group.objectIds : [])]
      .filter(id => objectIds.has(id) && !claimedIds.has(id));
    if (memberIds.length < 2) return normalizedGroups;
    memberIds.forEach(id => claimedIds.add(id));
    return [...normalizedGroups, { ...group, objectIds: memberIds }];
  }, []);

  return createLayoutDocument({
    canvas: value.canvas,
    settings: value.settings,
    objects: value.objects,
    conductors: value.conductors,
    nets: value.nets,
    layers: value.layers,
    groups,
  });
};

export const createLayoutObject = (type, id, variant = {}) => {
  if (type === 'chassis') {
    return {
      id,
      type,
      label: 'Amplifier Chassis',
      x: 45,
      y: 45,
      width: 300,
      height: 165,
      rotation: 0,
    };
  }

  if (type === 'turret-board') {
    return {
      id,
      type,
      label: 'Turret Board',
      x: 90,
      y: 105,
      width: 210,
      height: 50,
      rows: 2,
      columns: 12,
      turretSpacingX: 210 / 13,
      turretSpacingY: 50 / 3,
      rotation: 0,
    };
  }

  if (type === 'blank-turret-board') {
    return {
      id,
      type,
      label: 'Blank Turret Board',
      x: 90,
      y: 105,
      width: 210,
      height: 50,
      rotation: 0,
    };
  }

  if (type === 'turret') {
    return {
      id,
      type,
      label: '',
      x: 105,
      y: 120,
      width: 5,
      height: 5,
      rotation: 0,
    };
  }

  if (type === 'tube-socket') {
    const isPhenolicNoval = variant.construction === 'phenolic-noval';
    return {
      id,
      type,
      label: isPhenolicNoval ? 'Phenolic 9-pin Noval Socket' : '9-pin Tube Socket',
      ...(isPhenolicNoval ? { construction: 'phenolic-noval' } : {}),
      x: 180,
      y: 75,
      width: 36,
      height: 36,
      pinCount: 9,
      rotation: 0,
    };
  }

  if (type === 'octal-socket') {
    const isMcmurdo = variant.construction === 'mcmurdo';
    return {
      id,
      type,
      label: isMcmurdo ? 'McMurdo Octal Socket' : '8-pin Tube Socket',
      ...(isMcmurdo ? { construction: 'mcmurdo' } : {}),
      x: 225,
      y: 75,
      width: isMcmurdo ? 50 : 42,
      height: isMcmurdo ? 50 : 42,
      pinCount: 8,
      rotation: 0,
    };
  }

  if (type === 'potentiometer') {
    const isPec = variant.construction === 'pec';
    return {
      id,
      type,
      label: isPec ? 'PEC Potentiometer' : 'VR1',
      ...(isPec ? { construction: 'pec', bodyDiameter: 28, bodyDepth: 13 } : {}),
      value: '1MΩ',
      taper: 'Audio',
      x: 90,
      y: 55,
      width: isPec ? 32 : 40,
      height: 34,
      rotation: 0,
    };
  }

  if (type === 'impedance-selector') {
    return {
      id,
      type,
      label: 'Impedance Selector',
      value: '4Ω / 8Ω / 16Ω',
      x: 125,
      y: 55,
      width: 38,
      height: 38,
      rotation: 0,
    };
  }

  if (type === 'carling-toggle-switch') {
    const construction = ['spst', 'dpst', 'spdt', 'dpdt'].includes(variant.construction)
      ? variant.construction
      : 'spst';
    return {
      id,
      type,
      label: `Carling ${construction.toUpperCase()}`,
      construction,
      value: construction.toUpperCase(),
      x: 165,
      y: 55,
      width: 34,
      height: 38,
      rotation: 0,
    };
  }

  if (type === 'iec-power-inlet') {
    return {
      id,
      type,
      label: 'IEC Power Inlet',
      value: 'IEC C14',
      x: 205,
      y: 55,
      width: 44,
      height: 32,
      rotation: 0,
    };
  }

  if (type === 'panel-fuse-holder') {
    return {
      id,
      type,
      label: 'Fuse Holder',
      value: 'Panel mount',
      x: 250,
      y: 55,
      width: 34,
      height: 40,
      rotation: 0,
    };
  }

  if (type === 'input-jack') {
    return {
      id,
      type,
      label: 'J1',
      value: 'Switched mono',
      x: 285,
      y: 60,
      width: 24,
      height: 42,
      rotation: 0,
    };
  }

  if (type === 'parallel-output-jacks') {
    return {
      id,
      type,
      label: 'J1/J2',
      value: 'Parallel speaker outputs',
      x: 300,
      y: 125,
      width: 58,
      height: 42,
      rotation: 0,
    };
  }

  if (type === 'resistor') {
    const construction = variant.construction || 'carbon-composition';
    const definition = RESISTOR_LIBRARY[construction] || RESISTOR_LIBRARY['carbon-composition'];
    return {
      id,
      type,
      label: '',
      construction,
      value: definition.value,
      wattage: definition.wattage,
      x: 135,
      y: 135,
      width: definition.width,
      height: definition.height,
      rotation: 0,
    };
  }

  if (type === 'capacitor') {
    const construction = variant.construction || 'axial-film';
    const definition = CAPACITOR_LIBRARY[construction] || CAPACITOR_LIBRARY['axial-film'];
    return {
      id,
      type,
      label: '',
      construction,
      packageStyle: definition.packageStyle,
      value: definition.value,
      ...(definition.voltageRating ? { voltageRating: definition.voltageRating } : {}),
      ...(definition.sleeveColor ? { sleeveColor: definition.sleeveColor } : {}),
      x: 135,
      y: 155,
      width: definition.width,
      height: definition.height,
      ...(definition.packageStyle === 'can' ? { sections: DEFAULT_CAN_CAPACITOR_SECTIONS.map(section => ({ ...section })) } : {}),
      rotation: 0,
    };
  }

  if (type === 'diode') {
    const construction = variant.construction || 'rectifier';
    const definition = DIODE_LIBRARY[construction] || DIODE_LIBRARY.rectifier;
    return {
      id,
      type,
      label: 'D1',
      construction,
      value: definition.value,
      x: 135,
      y: 175,
      width: definition.width,
      height: definition.height,
      rotation: 0,
    };
  }

  if (type === 'power-transformer') {
    return {
      id,
      type,
      label: 'PT1',
      value: '18 W center tapped',
      mountStyle: 'laydown',
      taps: cloneDefaultPowerTransformerTaps(),
      x: 55,
      y: 55,
      width: 72,
      height: 62,
      rotation: 0,
    };
  }

  if (type === 'output-transformer') {
    return {
      id,
      type,
      label: 'OT1',
      value: '18 W output',
      mountStyle: 'standup',
      x: 300,
      y: 65,
      width: 58,
      height: 50,
      rotation: 0,
    };
  }

  if (type === 'choke') {
    return {
      id,
      type,
      label: 'L1',
      value: 'Filter choke',
      mountStyle: 'standup',
      x: 230,
      y: 60,
      width: 46,
      height: 40,
      rotation: 0,
    };
  }

  throw new Error(`Unknown layout object type: ${type}`);
};

const LAYOUT_REFERENCE_PREFIXES = Object.freeze({
  'tube-socket': 'V',
  'octal-socket': 'V',
  resistor: 'R',
  capacitor: 'C',
  diode: 'D',
  potentiometer: 'VR',
  'impedance-selector': 'S',
  'carling-toggle-switch': 'S',
  'iec-power-inlet': 'J',
  'panel-fuse-holder': 'F',
  'input-jack': 'J',
  'parallel-output-jacks': 'J',
  'power-transformer': 'PT',
  'output-transformer': 'OT',
  choke: 'L',
});

export const getNextLayoutReference = (document, type) => {
  const prefix = LAYOUT_REFERENCE_PREFIXES[type];
  if (!prefix) return null;
  const pattern = new RegExp(`(?:^|/)${prefix}(\\d+)(?=/|$)`, 'gi');
  const usedNumbers = document.objects.flatMap(object => {
    const matches = [...String(object.label || '').matchAll(pattern)];
    return matches.map(match => Number(match[1]));
  });
  const nextNumber = Math.max(0, ...usedNumbers) + 1;
  return type === 'parallel-output-jacks'
    ? `${prefix}${nextNumber}/${prefix}${nextNumber + 1}`
    : `${prefix}${nextNumber}`;
};

export const addLayoutObject = (document, object) => ({
  ...document,
  objects: [...document.objects, object],
});

export const expandLayoutCanvasToFit = (document) => {
  const majorSpacing = document.settings?.displayUnits === 'in' ? MM_PER_INCH : 10;
  const rightEdge = Math.max(0, ...document.objects.map(object => object.x + object.width));
  const bottomEdge = Math.max(0, ...document.objects.map(object => object.y + object.height));
  const width = rightEdge > document.canvas.width
    ? Math.ceil((rightEdge + majorSpacing) / majorSpacing) * majorSpacing
    : document.canvas.width;
  const height = bottomEdge > document.canvas.height
    ? Math.ceil((bottomEdge + majorSpacing) / majorSpacing) * majorSpacing
    : document.canvas.height;
  if (width === document.canvas.width && height === document.canvas.height) return document;
  return { ...document, canvas: { ...document.canvas, width, height } };
};

export const createLayoutGroup = (document, groupId, objectIds) => {
  const validObjectIds = new Set(document.objects.map(object => object.id));
  const members = [...new Set(objectIds)].filter(id => validObjectIds.has(id));
  if (members.length < 2) return document;
  const memberSet = new Set(members);
  const remainingGroups = document.groups.map(group => ({
    ...group,
    objectIds: group.objectIds.filter(id => !memberSet.has(id)),
  })).filter(group => group.objectIds.length >= 2);
  return { ...document, groups: [...remainingGroups, { id: groupId, objectIds: members }] };
};

export const removeLayoutGroup = (document, groupId) => ({
  ...document,
  groups: document.groups.filter(group => group.id !== groupId),
});

export const getLayoutGroupObjectIds = (document, objectId) => (
  document.groups.find(group => group.objectIds.includes(objectId))?.objectIds || [objectId]
);

export const duplicateLayoutObject = (document, objectId, duplicateId) => {
  const source = document.objects.find(object => object.id === objectId);
  if (!source) return document;
  const offset = document.canvas.grid;
  const duplicate = typeof window !== 'undefined' && typeof window.structuredClone === 'function'
    ? window.structuredClone(source)
    : JSON.parse(JSON.stringify(source));
  duplicate.id = duplicateId;
  duplicate.x = source.x + offset;
  duplicate.y = source.y + offset;
  return expandLayoutCanvasToFit(addLayoutObject(document, duplicate));
};

export const updateLayoutObject = (document, objectId, changes) => ({
  ...document,
  objects: document.objects.map(object => (
    object.id === objectId ? { ...object, ...changes } : object
  )),
});

export const updatePowerTransformerTaps = (document, objectId, taps) => {
  const object = document.objects.find(item => item.id === objectId);
  if (!object || object.type !== 'power-transformer') return document;
  const nextTapIds = new Set([...taps.top, ...taps.bottom].map(tap => tap.id));
  return {
    ...updateLayoutObject(document, objectId, { taps }),
    conductors: document.conductors.filter(conductor => (
      conductor.from.objectId !== objectId || nextTapIds.has(conductor.from.terminalId)
    ) && (
      conductor.to.objectId !== objectId || nextTapIds.has(conductor.to.terminalId)
    )),
  };
};

export const updateCanCapacitorSections = (document, objectId, sections) => {
  const object = document.objects.find(item => item.id === objectId);
  if (!object || object.type !== 'capacitor' || object.packageStyle !== 'can') return document;
  const nextSectionIds = new Set(sections.map(section => section.id));
  return {
    ...updateLayoutObject(document, objectId, {
      sections,
      value: sections.map(section => section.capacitance).join(' / '),
    }),
    conductors: document.conductors.filter(conductor => (
      conductor.from.objectId !== objectId
      || conductor.from.terminalId === 'common'
      || nextSectionIds.has(conductor.from.terminalId)
    ) && (
      conductor.to.objectId !== objectId
      || conductor.to.terminalId === 'common'
      || nextSectionIds.has(conductor.to.terminalId)
    )),
  };
};

export const removeLayoutObject = (document, objectId) => ({
  ...document,
  objects: document.objects.filter(object => object.id !== objectId).map(object => {
    if (!object.leads) return object;
    const leads = Object.fromEntries(Object.entries(object.leads).map(([terminalId, lead]) => {
      if (lead.connection?.objectId !== objectId) return [terminalId, lead];
      const { connection, ...detachedLead } = lead;
      return [terminalId, detachedLead];
    }));
    return { ...object, leads };
  }),
  conductors: document.conductors.filter(conductor => (
    conductor.from.objectId !== objectId && conductor.to.objectId !== objectId
  )),
  groups: document.groups.map(group => ({
    ...group,
    objectIds: group.objectIds.filter(id => id !== objectId),
  })).filter(group => group.objectIds.length >= 2),
});

export const getTurretGridGeometry = (object) => {
  const columns = Math.max(2, object.columns || 2);
  const rows = Math.max(1, object.rows || 1);
  const defaultSpacingX = object.width / (columns + 1);
  const defaultSpacingY = object.height / (rows + 1);
  const requestedSpacingX = Number(object.turretSpacingX) > 0 ? Number(object.turretSpacingX) : defaultSpacingX;
  const requestedSpacingY = Number(object.turretSpacingY) > 0 ? Number(object.turretSpacingY) : defaultSpacingY;
  const spacingX = Math.min(requestedSpacingX, Math.max(0, (object.width - 5) / (columns - 1)));
  const spacingY = rows === 1
    ? requestedSpacingY
    : Math.min(requestedSpacingY, Math.max(0, (object.height - 5) / (rows - 1)));
  return {
    columns,
    rows,
    spacingX,
    spacingY,
    startX: (object.width - (columns - 1) * spacingX) / 2,
    startY: rows === 1 ? object.height / 2 : (object.height - (rows - 1) * spacingY) / 2,
  };
};

export const getAxialLeadGeometry = (object, terminalId, bodyAnchor) => {
  const defaultTerminal = terminalId === 'lead-1' || terminalId === 'anode'
    ? { x: 0, y: object.height / 2 }
    : { x: object.width, y: object.height / 2 };
  const saved = object.leads?.[terminalId];
  const terminal = saved?.terminal && Number.isFinite(saved.terminal.x) && Number.isFinite(saved.terminal.y)
    ? saved.terminal
    : defaultTerminal;
  const hinge = saved?.hinge && Number.isFinite(saved.hinge.x) && Number.isFinite(saved.hinge.y)
    ? saved.hinge
    : {
      x: bodyAnchor.x + (terminal.x - bodyAnchor.x) / 2,
      y: bodyAnchor.y + (terminal.y - bodyAnchor.y) / 2,
    };
  return { bodyAnchor, hinge, terminal };
};

export const getObjectTerminals = (object) => {
  if (object.type === 'power-transformer') {
    const taps = getPowerTransformerTaps(object);
    const leads = [...taps.top.map(tap => ({ ...tap, side: 'top' })), ...taps.bottom.map(tap => ({ ...tap, side: 'bottom' }))];
    if (object.mountStyle === 'standup') {
      return leads.map(lead => {
        const isInput = lead.side === 'bottom';
        const group = leads.filter(item => (item.side === 'bottom') === isInput);
        const groupIndex = group.findIndex(item => item.id === lead.id);
        return {
          ...lead,
          label: `${lead.label} ${lead.specification} V`,
          x: isInput ? 0 : object.width,
          y: object.height * ((groupIndex + 1) / (group.length + 1)),
        };
      });
    }
    return leads.map(lead => {
      const group = lead.side === 'top' ? taps.top : taps.bottom;
      const groupIndex = group.findIndex(item => item.id === lead.id);
      return {
        ...lead,
        label: `${lead.label} ${lead.specification} V`,
        x: object.width * ((groupIndex + 1) / (group.length + 1)),
        y: lead.side === 'top' ? 0 : object.height,
      };
    });
  }

  if (object.type === 'output-transformer') {
    const leads = [
      { id: 'plate-1', label: 'Plate 1', specification: 'PLATE' },
      { id: 'primary-center-tap', label: 'Primary center tap', specification: 'CT' },
      { id: 'plate-2', label: 'Plate 2', specification: 'PLATE' },
      { id: 'secondary-common', label: 'Secondary common', specification: 'COM' },
      { id: 'secondary-4-ohm', label: '4 ohm tap', specification: '4Ω' },
      { id: 'secondary-8-ohm', label: '8 ohm tap', specification: '8Ω' },
      { id: 'secondary-16-ohm', label: '16 ohm tap', specification: '16Ω' },
    ];
    if (object.mountStyle === 'laydown') {
      return leads.map((lead, index) => ({
        ...lead,
        x: object.width * (index < 3 ? 0.24 + index * 0.26 : 0.12 + (index - 3) * 0.25),
        y: index < 3 ? 0 : object.height,
      }));
    }
    return leads.map((lead, index) => ({
      ...lead,
      x: index < 3 ? 0 : object.width,
      y: object.height * (index < 3 ? 0.28 + index * 0.22 : 0.18 + (index - 3) * 0.22),
    }));
  }

  if (object.type === 'choke') {
    return [
      { id: 'lead-1', label: 'Lead 1', x: 0, y: object.height / 2 },
      { id: 'lead-2', label: 'Lead 2', x: object.width, y: object.height / 2 },
    ];
  }

  if (object.type === 'potentiometer') {
    if (object.construction === 'pec') {
      return [
        { id: 'lug-1', label: 'Lug 1', x: 9, y: 0 },
        { id: 'lug-2', label: 'Lug 2 (wiper)', x: 16, y: 0 },
        { id: 'lug-3', label: 'Lug 3', x: 23, y: 0 },
      ];
    }
    return [
      { id: 'lug-1', label: 'Lug 1', x: object.width * 0.2, y: 0 },
      { id: 'lug-2', label: 'Lug 2 (wiper)', x: object.width * 0.5, y: 0 },
      { id: 'lug-3', label: 'Lug 3', x: object.width * 0.8, y: 0 },
    ];
  }

  if (object.type === 'impedance-selector') {
    return [
      { id: 'input-3', label: 'Input 3', specification: '16Ω', x: 4, y: 7 },
      { id: 'input-2', label: 'Input 2', specification: '8Ω', x: 19, y: 0 },
      { id: 'input-1', label: 'Input 1', specification: '4Ω', x: 33, y: 6 },
      { id: 'output', label: 'Output', specification: 'OUT', x: 38, y: 20 },
    ];
  }

  if (object.type === 'carling-toggle-switch') {
    if (object.construction === 'spdt') {
      return [
        { id: 'throw-1', label: 'Throw 1', x: 8, y: 27 },
        { id: 'common', label: 'Common', x: 17, y: 34 },
        { id: 'throw-2', label: 'Throw 2', x: 26, y: 27 },
      ];
    }
    if (object.construction === 'dpst') {
      return [
        { id: 'pole-1-line', label: 'Pole 1 line', x: 9, y: 25 },
        { id: 'pole-2-line', label: 'Pole 2 line', x: 25, y: 25 },
        { id: 'pole-1-load', label: 'Pole 1 load', x: 9, y: 34 },
        { id: 'pole-2-load', label: 'Pole 2 load', x: 25, y: 34 },
      ];
    }
    if (object.construction === 'dpdt') {
      return [
        { id: 'pole-1-throw-1', label: 'Pole 1 throw 1', x: 7, y: 24 },
        { id: 'pole-1-common', label: 'Pole 1 common', x: 17, y: 24 },
        { id: 'pole-1-throw-2', label: 'Pole 1 throw 2', x: 27, y: 24 },
        { id: 'pole-2-throw-1', label: 'Pole 2 throw 1', x: 7, y: 34 },
        { id: 'pole-2-common', label: 'Pole 2 common', x: 17, y: 34 },
        { id: 'pole-2-throw-2', label: 'Pole 2 throw 2', x: 27, y: 34 },
      ];
    }
    return [
      { id: 'line', label: 'Line', x: 9, y: 31 },
      { id: 'load', label: 'Load', x: 25, y: 31 },
    ];
  }

  if (object.type === 'iec-power-inlet') {
    return [
      { id: 'protective-earth', label: 'Protective earth', specification: 'PE', x: 22, y: 7 },
      { id: 'neutral', label: 'Neutral', specification: 'N', x: 14, y: 18 },
      { id: 'line', label: 'Line', specification: 'L', x: 30, y: 18 },
    ];
  }

  if (object.type === 'panel-fuse-holder') {
    return [
      { id: 'center-contact', label: 'Center contact', x: 17, y: 38 },
      { id: 'side-contact', label: 'Side contact', x: 0, y: 11 },
    ];
  }

  if (object.type === 'input-jack') {
    return [
      { id: 'tip', label: 'Tip', x: object.width, y: object.height * 0.68 },
      { id: 'sleeve', label: 'Sleeve', x: object.width, y: object.height * 0.34 },
      { id: 'switch', label: 'Tip switch', x: 0, y: object.height * 0.68 },
      { id: 'sleeve-switch', label: 'Sleeve switch', x: 0, y: object.height * 0.34 },
    ];
  }

  if (object.type === 'parallel-output-jacks') {
    return [
      { id: 'jack-1-sleeve', label: 'Jack 1 sleeve', x: object.width * 0.22, y: object.height * 0.22 },
      { id: 'jack-1-tip', label: 'Jack 1 tip', x: object.width * 0.22, y: object.height * 0.78 },
      { id: 'jack-2-sleeve', label: 'Jack 2 sleeve', x: object.width * 0.78, y: object.height * 0.22 },
      { id: 'jack-2-tip', label: 'Jack 2 tip', x: object.width * 0.78, y: object.height * 0.78 },
    ];
  }

  if (object.type === 'capacitor' && object.construction === 'radial-electrolytic') {
    return [
      { id: 'lead-1', label: 'Negative lead', polarity: 'negative', x: object.width * 0.32, y: object.height },
      { id: 'lead-2', label: 'Positive lead', polarity: 'positive', x: object.width * 0.68, y: object.height },
    ];
  }

  if (object.type === 'diode') {
    return [
      { id: 'anode', label: 'Anode', polarity: 'anode', ...getAxialLeadGeometry(object, 'anode', { x: 0, y: object.height / 2 }).terminal },
      { id: 'cathode', label: 'Cathode', polarity: 'cathode', ...getAxialLeadGeometry(object, 'cathode', { x: object.width, y: object.height / 2 }).terminal },
    ];
  }

  if (object.type === 'capacitor' && object.packageStyle === 'radial') {
    return [
      { id: 'lead-1', label: 'Lead 1', x: object.width * 0.28, y: object.height },
      { id: 'lead-2', label: 'Lead 2', x: object.width * 0.72, y: object.height },
    ];
  }

  if (object.type === 'capacitor' && object.packageStyle === 'can') {
    if (Array.isArray(object.sections) && object.sections.length > 0) {
      const sectionCount = object.sections.length;
      const sectionTerminals = object.sections.map((section, index) => ({
          id: section.id,
          label: `${section.label} ${section.capacitance}`,
          x: object.width * (sectionCount === 1 ? 0.28 : 0.24 + (0.52 * index / (sectionCount - 1))),
          y: object.height * 0.62,
        }));
      return [
        ...sectionTerminals,
        {
          id: 'common',
          label: 'Common negative',
          x: object.width * (sectionCount === 1 ? 0.72 : 0.5),
          y: object.height * (sectionCount === 1 ? 0.62 : 0.74),
        },
      ];
    }
    return [
      { id: 'lead-1', label: 'Negative terminal', x: object.width * 0.36, y: object.height * 0.56 },
      { id: 'lead-2', label: 'Positive terminal', x: object.width * 0.64, y: object.height * 0.56 },
    ];
  }

  if (object.type === 'capacitor' && ['board-axial-electrolytic', 'sprague-30d-electrolytic', 'axial-electrolytic'].includes(object.construction)) {
    return [
      { id: 'lead-1', label: 'Negative lead', polarity: 'negative', ...getAxialLeadGeometry(object, 'lead-1', { x: 0, y: object.height / 2 }).terminal },
      { id: 'lead-2', label: 'Positive lead', polarity: 'positive', ...getAxialLeadGeometry(object, 'lead-2', { x: object.width, y: object.height / 2 }).terminal },
    ];
  }

  if (object.type === 'resistor' || object.type === 'capacitor') {
    return [
      { id: 'lead-1', label: 'Lead 1', ...getAxialLeadGeometry(object, 'lead-1', { x: 0, y: object.height / 2 }).terminal },
      { id: 'lead-2', label: 'Lead 2', ...getAxialLeadGeometry(object, 'lead-2', { x: object.width, y: object.height / 2 }).terminal },
    ];
  }

  if (object.type === 'turret-board') {
    const { columns, rows, spacingX, spacingY, startX, startY } = getTurretGridGeometry(object);
    const terminals = [];
    for (let row = 1; row <= rows; row += 1) {
      for (let column = 1; column <= columns; column += 1) {
        terminals.push({
          id: `r${row}c${column}`,
          label: `Row ${row}, turret ${column}`,
          x: startX + (column - 1) * spacingX,
          y: startY + (row - 1) * spacingY,
        });
      }
    }
    return terminals;
  }

  if (object.type === 'turret') {
    return [{ id: 'terminal', label: 'Terminal', x: object.width / 2, y: object.height / 2 }];
  }

  if (object.type === 'tube-socket' || object.type === 'octal-socket') {
    const pinCount = Math.max(4, object.pinCount || 9);
    const radius = object.type === 'octal-socket' && object.construction === 'mcmurdo'
      ? 13.5
      : Math.min(object.width, object.height) * 0.32;
    return Array.from({ length: pinCount }, (_, index) => {
      const angle = (-90 + (index * 360 / pinCount)) * Math.PI / 180;
      return {
        id: `pin-${index + 1}`,
        label: `Pin ${index + 1}`,
        x: object.width / 2 + Math.cos(angle) * radius,
        y: object.height / 2 + Math.sin(angle) * radius,
      };
    });
  }

  return [];
};

export const resolveTerminalPosition = (document, endpoint) => {
  const object = document.objects.find(item => item.id === endpoint.objectId);
  if (!object) return null;
  const terminal = getObjectTerminals(object).find(item => item.id === endpoint.terminalId);
  if (!terminal) return null;

  const rotation = (object.rotation || 0) * Math.PI / 180;
  const centerX = object.width / 2;
  const centerY = object.height / 2;
  const localX = terminal.x - centerX;
  const localY = terminal.y - centerY;
  return {
    x: object.x + centerX + localX * Math.cos(rotation) - localY * Math.sin(rotation),
    y: object.y + centerY + localX * Math.sin(rotation) + localY * Math.cos(rotation),
  };
};

export const createConductor = (id, from, to, color = BRIGHT_WIRE_COLORS[0]) => ({
  id,
  from,
  to,
  color,
  gauge: 22,
});

export const getConductorCurveControls = (conductor, from, to) => {
  const bendX = (from.x + to.x) / 2;
  return {
    control1: {
      x: bendX + (conductor.curve?.control1?.x || 0),
      y: from.y + (conductor.curve?.control1?.y || 0),
    },
    control2: {
      x: bendX + (conductor.curve?.control2?.x || 0),
      y: to.y + (conductor.curve?.control2?.y || 0),
    },
  };
};

export const getCubicCurvePoint = (from, control1, control2, to, parameter) => {
  const inverse = 1 - parameter;
  return {
    x: inverse ** 3 * from.x
      + 3 * inverse ** 2 * parameter * control1.x
      + 3 * inverse * parameter ** 2 * control2.x
      + parameter ** 3 * to.x,
    y: inverse ** 3 * from.y
      + 3 * inverse ** 2 * parameter * control1.y
      + 3 * inverse * parameter ** 2 * control2.y
      + parameter ** 3 * to.y,
  };
};

export const findClosestCurveParameter = (point, from, control1, control2, to) => {
  let closestParameter = 0;
  let closestDistance = Infinity;
  for (let step = 0; step <= 100; step += 1) {
    const parameter = step / 100;
    const curvePoint = getCubicCurvePoint(from, control1, control2, to, parameter);
    const distance = (curvePoint.x - point.x) ** 2 + (curvePoint.y - point.y) ** 2;
    if (distance < closestDistance) {
      closestDistance = distance;
      closestParameter = parameter;
    }
  }
  return Math.min(Math.max(closestParameter, 0.02), 0.98);
};

export const reshapeConductorCurve = (conductor, parameter, delta) => {
  const inverse = 1 - parameter;
  const control1Weight = 3 * inverse ** 2 * parameter;
  const control2Weight = 3 * inverse * parameter ** 2;
  const denominator = control1Weight ** 2 + control2Weight ** 2;
  const control1 = conductor.curve?.control1 || { x: 0, y: 0 };
  const control2 = conductor.curve?.control2 || { x: 0, y: 0 };
  return {
    control1: {
      x: control1.x + delta.x * control1Weight / denominator,
      y: control1.y + delta.y * control1Weight / denominator,
    },
    control2: {
      x: control2.x + delta.x * control2Weight / denominator,
      y: control2.y + delta.y * control2Weight / denominator,
    },
  };
};

export const braidConductors = (document, conductorIds, braidId) => {
  const ids = [...new Set(conductorIds)].filter(id => document.conductors.some(conductor => conductor.id === id));
  if (ids.length !== 2) return document;
  const replacedBraidIds = new Set(document.conductors
    .filter(conductor => ids.includes(conductor.id) && conductor.braid?.id)
    .map(conductor => conductor.braid.id));
  return {
    ...document,
    conductors: document.conductors.map(conductor => {
      if (ids.includes(conductor.id)) return { ...conductor, braid: { id: braidId, index: ids.indexOf(conductor.id) } };
      if (!replacedBraidIds.has(conductor.braid?.id)) return conductor;
      const { braid, ...unbraided } = conductor;
      return unbraided;
    }),
  };
};

export const unbraidConductors = (document, conductorIds) => {
  const ids = new Set(conductorIds);
  return {
    ...document,
    conductors: document.conductors.map(conductor => {
      if (!ids.has(conductor.id) || !conductor.braid) return conductor;
      const { braid, ...unbraided } = conductor;
      return unbraided;
    }),
  };
};

export const getBraidedConductorPoints = (document, conductor, partner, sampleCount = 96) => {
  const endpoints = [conductor, partner].map(item => ({
    from: resolveTerminalPosition(document, item.from),
    to: resolveTerminalPosition(document, item.to),
  }));
  if (endpoints.some(({ from, to }) => !from || !to)) return [];
  const controls = [conductor, partner].map((item, index) => (
    getConductorCurveControls(item, endpoints[index].from, endpoints[index].to)
  ));
  const distance = (left, right) => Math.hypot(left.x - right.x, left.y - right.y);
  const sameDirection = distance(endpoints[0].from, endpoints[1].from) + distance(endpoints[0].to, endpoints[1].to);
  const oppositeDirection = distance(endpoints[0].from, endpoints[1].to) + distance(endpoints[0].to, endpoints[1].from);
  if (oppositeDirection < sameDirection) {
    [endpoints[1].from, endpoints[1].to] = [endpoints[1].to, endpoints[1].from];
    [controls[1].control1, controls[1].control2] = [controls[1].control2, controls[1].control1];
  }
  const phase = conductor.braid?.index === 1 ? Math.PI : 0;
  const amplitude = 2.7;
  const twistPitch = 10;
  const unbraidedLeadLength = MM_PER_INCH;
  const transitionLength = MM_PER_INCH / 4;
  const samples = Array.from({ length: sampleCount + 1 }, (_, step) => {
    const parameter = step / sampleCount;
    const ownPoint = getCubicCurvePoint(
      endpoints[0].from, controls[0].control1, controls[0].control2, endpoints[0].to, parameter
    );
    const partnerPoint = getCubicCurvePoint(
      endpoints[1].from, controls[1].control1, controls[1].control2, endpoints[1].to, parameter
    );
    return {
      ownPoint,
      center: { x: (ownPoint.x + partnerPoint.x) / 2, y: (ownPoint.y + partnerPoint.y) / 2 },
    };
  });
  const distances = samples.reduce((values, sample, index) => {
    if (index === 0) return [0];
    return [...values, values[index - 1] + distance(samples[index - 1].center, sample.center)];
  }, []);
  const totalLength = distances[distances.length - 1];

  return samples.map((sample, index) => {
    const distanceFromStart = distances[index];
    const distanceFromEnd = totalLength - distanceFromStart;
    const braidRoom = Math.min(distanceFromStart, distanceFromEnd) - unbraidedLeadLength;
    const transition = Math.min(1, Math.max(0, braidRoom / transitionLength));
    const blend = transition * transition * (3 - 2 * transition);
    const before = samples[Math.max(0, index - 1)].center;
    const after = samples[Math.min(sampleCount, index + 1)].center;
    const tangentX = after.x - before.x;
    const tangentY = after.y - before.y;
    const tangentLength = Math.hypot(tangentX, tangentY) || 1;
    const braidedDistance = Math.max(0, distanceFromStart - unbraidedLeadLength);
    const offset = Math.sin(braidedDistance / twistPitch * Math.PI * 2 + phase) * amplitude * blend;
    return {
      x: sample.ownPoint.x * (1 - blend) + sample.center.x * blend - tangentY / tangentLength * offset,
      y: sample.ownPoint.y * (1 - blend) + sample.center.y * blend + tangentX / tangentLength * offset,
    };
  });
};

export const addConductor = (document, conductor) => ({
  ...document,
  conductors: [...document.conductors, conductor],
});

export const updateConductor = (document, conductorId, changes) => ({
  ...document,
  conductors: document.conductors.map(conductor => (
    conductor.id === conductorId ? { ...conductor, ...changes } : conductor
  )),
});

export const removeConductor = (document, conductorId) => {
  const removed = document.conductors.find(conductor => conductor.id === conductorId);
  return {
    ...document,
    conductors: document.conductors
      .filter(conductor => conductor.id !== conductorId)
      .map(conductor => {
        if (!removed?.braid?.id || conductor.braid?.id !== removed.braid.id) return conductor;
        const { braid, ...unbraided } = conductor;
        return unbraided;
      }),
  };
};

const defaultStackPosition = (kind, item) => {
  if (kind === 'object' && item.type === 'chassis') return 0;
  if (kind === 'conductor') return 100;
  return 200;
};

export const getLayoutStack = (document) => [
  ...document.objects.map((item, index) => ({
    kind: 'object',
    item,
    order: item.stackOrder ?? defaultStackPosition('object', item),
    sourceIndex: index,
  })),
  ...document.conductors.map((item, index) => ({
    kind: 'conductor',
    item,
    order: item.stackOrder ?? defaultStackPosition('conductor', item),
    sourceIndex: document.objects.length + index,
  })),
].sort((left, right) => left.order - right.order || left.sourceIndex - right.sourceIndex);

export const reorderLayoutItem = (document, kind, id, direction) => {
  const stack = getLayoutStack(document);
  const currentIndex = stack.findIndex(entry => entry.kind === kind && entry.item.id === id);
  if (currentIndex < 0) return document;

  let targetIndex = currentIndex;
  if (direction === 'front') targetIndex = stack.length - 1;
  if (direction === 'forward') targetIndex = Math.min(stack.length - 1, currentIndex + 1);
  if (direction === 'backward') targetIndex = Math.max(0, currentIndex - 1);
  if (direction === 'back') targetIndex = 0;
  if (targetIndex === currentIndex) return document;

  const [selected] = stack.splice(currentIndex, 1);
  stack.splice(targetIndex, 0, selected);
  const positions = new Map(stack.map((entry, index) => [`${entry.kind}:${entry.item.id}`, index]));

  return {
    ...document,
    objects: document.objects.map(object => ({
      ...object,
      stackOrder: positions.get(`object:${object.id}`),
    })),
    conductors: document.conductors.map(conductor => ({
      ...conductor,
      stackOrder: positions.get(`conductor:${conductor.id}`),
    })),
  };
};